import { useCallback, useEffect, useState } from "react";
import { Copy, Navigation, Send, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getOnce, detailMsg } from "@/lib/geo";
import { FormDialog } from "@/components/kids/kidsUi";
import { Field } from "@/components/common";
import { useLiveRefresh } from "@/lib/live";
import { LocMap } from "./LocMap";

const cls = "q-input min-h-[44px] w-full";
const linkOf = (e) => `${window.location.origin}${e.url_path}`;
const clock = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

async function sendToFamily(hub, e) {
  try {
    const { data } = await api.get(`/organizations/${hub.id}/connect/conversations`);
    const fam = (data.conversations || data).find((c) => c.kind === "family");
    await api.post(`/organizations/${hub.id}/connect/conversations/${fam.id}/messages`, { text: `My ETA to ${e.dest_name}: ${linkOf(e)}` });
    toast.success("Sent to the Whole family chat");
  } catch (err) { toast.error(detailMsg(err, "Couldn't send it in Messages")); }
}

function Links({ hub, e }) {
  const copy = () => navigator.clipboard?.writeText(linkOf(e)).then(() => toast.success("Link copied"), () => toast.error("Couldn't copy"));
  return (
    <div className="flex flex-wrap gap-2">
      <button className="q-btn q-btn-secondary min-h-[44px]" onClick={copy} data-testid={`eta-copy-${e.id}`}><Copy className="h-4 w-4" /> Copy link</button>
      {navigator.share && <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => navigator.share({ title: "My ETA", url: linkOf(e) }).catch(() => {})} data-testid={`eta-share-${e.id}`}><Share2 className="h-4 w-4" /> Share</button>}
      <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => sendToFamily(hub, e)} data-testid={`eta-send-msg-${e.id}`}><Send className="h-4 w-4" /> Send in Messages</button>
    </div>
  );
}

function NewEta({ hub, places, locOn, onClose, onDone }) {
  const [f, setF] = useState({ dest: "", minutes: "30", custom: "", withLoc: locOn });
  const [busy, setBusy] = useState(false);
  const dests = places.filter((p) => p.name !== "Home");
  const go = async () => {
    setBusy(true);
    const pos = f.withLoc ? await getOnce() : null;
    try {
      const home = places.find((p) => p.name === "Home");
      const { data } = await api.post(`/organizations/${hub.id}/eta`, { dest_place_id: f.dest || home?.id || null, minutes: f.minutes === "custom" ? null : Number(f.minutes),
        arrive_at: f.minutes === "custom" ? f.custom : null, ...(pos || {}) });
      if (f.withLoc && !pos) toast.warning("Location wasn't shared.");
      onDone(data);
    } catch (e) { toast.error(detailMsg(e, "Couldn't share your ETA")); setBusy(false); }
  };
  return (
    <FormDialog open onClose={onClose} title="Share my ETA" description="Anyone with the link sees your first name, where you're going, a countdown and the last location you shared. Nothing else." testid="eta-dialog">
      <div className="space-y-3">
        <Field label="Going to"><select className={cls} value={f.dest} onChange={(e) => setF({ ...f, dest: e.target.value })} data-testid="eta-dest"><option value="">Home 🏠</option>{dests.map((p) => <option key={p.id} value={p.id}>{p.name} {p.emoji}</option>)}</select></Field>
        <div className="grid grid-cols-4 gap-2" role="radiogroup">
          {["15", "30", "45", "custom"].map((m) => <button key={m} type="button" onClick={() => setF({ ...f, minutes: m })} className={`min-h-[44px] rounded-xl border text-sm font-semibold ${f.minutes === m ? "border-q-accent bg-q-raised text-q-text" : "border-q-line text-q-muted"}`} data-testid={`eta-min-${m}`}>{m === "custom" ? "Custom" : `${m} min`}</button>)}
        </div>
        {f.minutes === "custom" && <Field label="Arrive at"><input type="time" className={cls} value={f.custom} onChange={(e) => setF({ ...f, custom: e.target.value })} data-testid="eta-custom-time" /></Field>}
        {locOn && <label className="flex min-h-[44px] items-center gap-3 text-sm text-q-text"><input type="checkbox" className="h-5 w-5" checked={f.withLoc} onChange={(e) => setF({ ...f, withLoc: e.target.checked })} data-testid="eta-with-loc" /> Include my location right now (read once)</label>}
        <button className="q-btn q-btn-primary min-h-[48px] w-full" disabled={busy || (f.minutes === "custom" && !f.custom)} onClick={go} data-testid="eta-create-btn">{busy ? "Sharing…" : "Create link"}</button>
      </div>
    </FormDialog>
  );
}

function MyEta({ hub, e, locOn, reload }) {
  const [busy, setBusy] = useState(false);
  const act = async (path, body = {}) => {
    setBusy(true);
    try { await api.post(`/organizations/${hub.id}/eta/${e.id}/${path}`, body); reload(); } catch (err) { toast.error(detailMsg(err, "Couldn't update")); }
    setBusy(false);
  };
  const update = async () => { const pos = locOn ? await getOnce() : null; if (!pos) toast.warning("Location wasn't shared."); act("update", pos || {}); };
  return (
    <li className="q-card space-y-3 p-4" data-testid={`my-eta-${e.id}`}>
      <p className="font-semibold text-q-text">On my way to {e.dest_name} {e.dest_emoji} · arriving {clock(e.arrive_at)}</p>
      {e.location && <><LocMap lat={e.location.lat} lng={e.location.lng} height={140} testid={`my-eta-map-${e.id}`} /><p className="text-xs text-q-muted" data-testid={`my-eta-loc-time-${e.id}`}>Location shared at {clock(e.location.at)}</p></>}
      <Links hub={hub} e={e} />
      <div className="flex flex-wrap gap-2">
        <button className="q-btn q-btn-secondary min-h-[44px]" disabled={busy} onClick={update} data-testid={`eta-update-${e.id}`}><Navigation className="h-4 w-4" /> Update</button>
        <button className="q-btn q-btn-primary min-h-[44px]" disabled={busy} onClick={() => act("arrived")} data-testid={`eta-arrived-${e.id}`}>I've arrived</button>
        <button className="q-btn q-btn-ghost min-h-[44px]" disabled={busy} onClick={() => act("stop")} data-testid={`eta-stop-${e.id}`}>Stop sharing</button>
      </div>
    </li>
  );
}

export function EtaPanel({ hub, places, locOn, onChanged }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [made, setMade] = useState(null);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/eta`).then(({ data }) => setD(data)).catch(() => {}), [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.entity === "eta", 30000);
  const reload = () => { load(); onChanged?.(); };
  return (
    <section className="mb-6 space-y-3" data-testid="eta-panel">
      <button className="q-btn q-btn-secondary min-h-[48px]" onClick={() => setOpen(true)} data-testid="share-eta-btn"><Navigation className="h-4 w-4" /> Share my ETA</button>
      {made && <div className="q-card space-y-2 p-4" data-testid="eta-created"><p className="text-sm font-semibold text-q-text">Your ETA link is ready. The app doesn't send texts, so share it your way.</p><Links hub={hub} e={made} /></div>}
      {d?.mine.length > 0 && <ul className="space-y-2" data-testid="my-etas">{d.mine.map((e) => <MyEta key={e.id} hub={hub} e={e} locOn={locOn} reload={reload} />)}</ul>}
      {d?.family.length > 0 && <ul className="space-y-2" data-testid="family-etas">{d.family.map((e) => (
        <li key={e.id} className="q-card p-3 text-sm text-q-text" data-testid={`family-eta-${e.id}`}>{e.who_name} is on the way to {e.dest_name} {e.dest_emoji} · arriving {clock(e.arrive_at)}
          {e.location && <div className="mt-2"><LocMap lat={e.location.lat} lng={e.location.lng} height={120} testid={`family-eta-map-${e.id}`} /></div>}</li>))}</ul>}
      {open && <NewEta hub={hub} places={places} locOn={locOn} onClose={() => setOpen(false)} onDone={(e) => { setOpen(false); setMade(e); reload(); }} />}
    </section>
  );
}
